import { useRouter, useRouterState } from "@tanstack/react-router";
import { useCallback, useMemo, type ReactNode } from "react";
import {
  AppFrame,
  AppNavigation,
  NavigationProvider,
  placeOf,
  TooltipProvider,
  TopBar,
  useResume,
} from "@optymyse/suite-ui";
import { squares, useApps } from "../../lib/apps";
import { meUser, useMe, useSignOut } from "../../lib/auth";
import { centralUrl, currentWorkspaceSlug } from "../../lib/hosts";
import { linkNavigation } from "../../lib/navigation";
import { hasWorkspacesToChoose, organisationIcon, peopleViewer, seesPeople, useWorkspace } from "../../lib/workspaces";
import { APP_NAME, PRODUCT_KEY } from "../../../shared/product";

/**
 * A workspace's console in the suite's frame (Optymyse/SuiteUi): the top bar with the squares, the workspace and the
 * account (Light, Dark or Device, and signing out of the suite), and the suite's navigation, Link's areas in a rail
 * with each area's sections beside it. Each area goes back to the section last open there for this person in this
 * workspace, and another app's squares open Link where the person last was (#resume).
 *
 * The workspace is its organisation's name as Impa holds it, never its address, with its icon logo where Impa holds one
 * (the kit's building where it holds none), as every app of the suite shows it. With more than one workspace, or some
 * Optymyse couldn't be asked about just now, its menu lists every workspace the person has in Link, each with its logo,
 * moving to one by loading its host, then "All your workspaces", the choice of them on the central host. With one, and
 * none unchecked, the bar names it without a menu, as Assure's does: the central host would only send them back here.
 */
export function LinkFrame({ children }: { children: ReactNode }) {
  const router = useRouter();
  const location = useRouterState({ select: (state) => state.location });
  const slug = currentWorkspaceSlug()!;
  const me = useMe();
  const user = meUser(me);
  const view = useWorkspace(slug);
  const apps = useApps();
  const signOut = useSignOut();
  const navigate = useCallback((href: string) => router.history.push(href), [router]);
  const workspaces = me.data?.kind === "signed_in" ? me.data.me.workspaces : [];
  const here = workspaces.find((workspace) => workspace.slug === slug);
  // The central host's choice of workspaces, only where it has one to show (hasWorkspacesToChoose).
  const allWorkspaces = hasWorkspacesToChoose(me.data) ? centralUrl("/admin") : undefined;

  const navigation = useMemo(
    () =>
      linkNavigation({
        administers: view.data?.role === "administrator",
        seesPeople: seesPeople(peopleViewer(view.data, user?.id)),
      }),
    [view.data, user],
  );
  const everyArea = useMemo(() => [...navigation.areas, navigation.settings], [navigation]);
  const scope = `${view.data?.workspace.id ?? slug}:${user?.id ?? "unknown"}`;
  useResume({
    scope,
    path: `${location.pathname}${location.searchStr}`,
    home: "/admin",
    accept: (address) => placeOf(everyArea, new URL(address, "http://localhost").pathname) !== null,
    replace: (address) => router.history.replace(address),
  });

  return (
    <NavigationProvider navigate={navigate}>
      <TooltipProvider>
        <AppFrame
          topBar={
            <TopBar
              product={PRODUCT_KEY}
              homeHref="/admin"
              apps={squares(apps)}
              organisation={
                view.data
                  ? {
                      id: view.data.workspace.id,
                      name: view.data.workspace.name,
                      icon: organisationIcon(view.data.workspace.logos),
                    }
                  : here
                    ? { id: here.id, name: here.name, icon: organisationIcon(here.logos) }
                    : undefined
              }
              organisations={workspaces.map((workspace) => ({
                id: workspace.id,
                name: workspace.name,
                icon: organisationIcon(workspace.logos),
              }))}
              onOrganisationChange={(id) => {
                // Each workspace is on its own host, so moving to another is a page load.
                const chosen = workspaces.find((workspace) => workspace.id === id);
                if (chosen && chosen.slug !== slug) window.location.href = chosen.console_url;
              }}
              manageOrganisationsHref={allWorkspaces}
              manageOrganisationsLabel={allWorkspaces === undefined ? undefined : "All your workspaces"}
              account={{ name: user?.name ?? "", email: user?.email ?? "", onSignOut: () => signOut.mutate() }}
            />
          }
          navigation={
            <AppNavigation
              label={APP_NAME}
              areas={navigation.areas}
              settings={navigation.settings}
              place={placeOf(everyArea, location.pathname)}
              remember={scope}
            />
          }
        >
          {children}
        </AppFrame>
      </TooltipProvider>
    </NavigationProvider>
  );
}
